/**
 * RTL Language Detection Utility
 * 
 * This utility helps determine if a language is RTL (Right-to-Left)
 * and provides helper functions for RTL detection.
 */

// List of RTL language codes
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur']; // Arabic, Hebrew, Persian/Farsi, Urdu

/**
 * Check if a language code is RTL
 * @param {string} languageCode - ISO 639-1 language code (e.g., 'ar', 'en')
 * @returns {boolean} - True if the language is RTL
 */
export const isRTLanguage = (languageCode) => {
  if (!languageCode) return false;
  return RTL_LANGUAGES.includes(languageCode.toLowerCase());
};

/**
 * Check if any of the provided language codes are RTL
 * @param {string[]} languageCodes - Array of ISO 639-1 language codes
 * @returns {boolean} - True if any language is RTL
 */
export const hasAnyRTLanguage = (languageCodes) => {
  if (!languageCodes || !Array.isArray(languageCodes)) return false;
  return languageCodes.some(code => isRTLanguage(code));
};

/**
 * Set the document direction based on language code
 * @param {string} languageCode - ISO 639-1 language code
 */
export const setDocumentDirection = (languageCode) => {
  if (typeof document !== 'undefined') {
    document.documentElement.dir = isRTLanguage(languageCode) ? 'rtl' : 'ltr';
  }
};

/**
 * Get the current document direction
 * @returns {string} - 'rtl' or 'ltr'
 */
export const getDocumentDirection = () => {
  if (typeof document !== 'undefined') {
    return document.documentElement.dir || 'ltr';
  }
  return 'ltr';
};

/**
 * Check if the current document is in RTL mode
 * @returns {boolean} - True if document direction is RTL
 */
export const isRTL = () => {
  return getDocumentDirection() === 'rtl';
};
